import React, { useCallback, type MouseEvent, type KeyboardEvent, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import AddItemIcon from '@atlaskit/icon/core/migration/shortcut--add-item';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { CustomItem, Section } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { expValEquals, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsSimplifyNavExperimentEnabled } from '@atlassian/jira-onboarding-simplify-navigation-experiment/src/common/utils/is-simplify-nav-experiment-enabled/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';
import { getFeatureMap } from '../../../common/constants.tsx';
import { FeatureToggleItem } from './feature-item/index.tsx';
import type { ItemToggleProps } from './feature-item/types.tsx';
import messages from './messages.tsx';
import type { FeatureToggleGroupProps } from './types.tsx';

export const FeatureToggleGroup = ({
	features,
	projectUrl,
	onItemToggle,
	onMoreFeatureClick,
	onAddShortcutClick,
}: FeatureToggleGroupProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [expConfig] = UNSAFE_noExposureExp('jsw_simplify_navigation_m2');

	const featureMap = getFeatureMap({
		isSimplifyNavExperimentEnabled: expConfig.get('isEnabled', false),
		isListViewEnabled: expValEquals('jsw_list_view', 'cohort', 'variation'),
	});

	const featuresToBeShown = useMemo(
		() => features.filter((f) => !featureMap[f.feature]?.isHidden),
		[features, featureMap],
	);

	const isSimplifyNavExperimentEnabled = fg('jsw_simplify_navigation_m2_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsSimplifyNavExperimentEnabled()
		: false;

	const ufoPressTracing = usePressTracing('next-gen-navigator-feature-toggled');

	const handleMoreFeatureClick = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			onMoreFeatureClick(e);
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'moreFeatures');
		},
		[createAnalyticsEvent, onMoreFeatureClick],
	);

	const handleAddShortcutClick = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			onAddShortcutClick?.(e);
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'addShortcut');
		},
		[createAnalyticsEvent, onAddShortcutClick],
	);

	const handleItemToggle = useCallback(
		({ featureId, state, analyticsEvent }: ItemToggleProps) => {
			ufoPressTracing();
			onItemToggle(featureId);
			fireUIAnalytics(analyticsEvent, 'featureToggle', {
				toggleTo: state === 'ENABLED' ? 'DISABLED' : 'ENABLED',
				otherFeatures: Object.fromEntries(
					features.filter((f) => f.feature !== featureId).map((f) => [f.feature, f.state]),
				),
				itemKey: featureId,
			});
		},
		[features, onItemToggle, ufoPressTracing],
	);

	return (
		<>
			{featuresToBeShown.map((f) => (
				<FeatureToggleItem key={f.feature} {...f} onItemToggle={handleItemToggle} />
			))}
			<Section hasSeparator={!(featuresToBeShown.length === 0)}>
				{isSimplifyNavExperimentEnabled ? (
					<>
						<CustomItem
							component={Button}
							key="shortcuts-add"
							onClick={handleAddShortcutClick}
							iconBefore={<AddItemIcon spacing="spacious" label="" />}
						>
							{formatMessage(messages.addItem)}
						</CustomItem>
						<CustomItem
							key="more-feature"
							component={MoreFeatureLinkSimplifyNav}
							href={`${projectUrl}/settings/features`}
							onClick={handleMoreFeatureClick}
							iconBefore={<EditorAddIcon spacing="spacious" label="" />}
						>
							{formatMessage(messages.moreFeaturesButton)}
						</CustomItem>
					</>
				) : (
					<CustomItem
						key="more-feature"
						component={MoreFeatureLink}
						href={`${projectUrl}/settings/features`}
						onClick={handleMoreFeatureClick}
					>
						{formatMessage(messages.moreViewsButton)}
					</CustomItem>
				)}
			</Section>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MoreFeatureLink = styled(Link)({
	paddingBlock: token('space.100'),
	paddingInline: token('space.200'),
	borderRadius: 'inherit',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MoreFeatureLinkSimplifyNav = styled(Link)({
	paddingBlock: token('space.100'),
	paddingInline: token('space.100'),
	borderRadius: 'inherit',
});
